import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { inventoryApi } from '../api/inventory-api-instance';
import { ErrorState } from '../components/ErrorState';
import { AppliedFilters } from '../components/AppliedFilters';
import { PageControls } from '../components/PageControls';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { SearchableSelect } from '../components/SearchableSelect';
import { StatusBadge } from '../components/StatusBadge';
import { TableSkeleton } from '../components/TableSkeleton';
import {
  itemStatuses,
  type Customer,
  type Item,
  type ItemStatus,
  type ItemUsage,
  type Part,
  type Site,
} from '../domain/inventory';
import { statusFamilies, statusLabels, type StatusFamily } from '../domain/status';
import {
  familyOrder,
  FULL_FLEET_ITEMS_QUERY_KEY,
  FULL_FLEET_PAGE_SIZE,
} from '../features/dashboard/aggregations';
import { BatchUpdateDrawer, type BatchChange } from '../features/batch-update/BatchUpdateDrawer';
import { DeleteItemsDialog } from '../features/item-delete/DeleteItemsDialog';
import {
  addSavedView,
  loadSavedViews,
  removeSavedView,
  type SavedView,
} from '../features/inventory/saved-views';
import { sitesForCustomers } from '../features/inventory/customer-site-filter';
import {
  attentionSourceLabels,
  attentionSourceValues,
  type AttentionSource,
} from '../features/inventory/attention-source';
import { ExportDialog } from '../features/inventory/ExportDialog';
import {
  filterInventoryItems,
  type CustomerInventoryScope,
} from '../features/inventory/item-filtering';
import { InventoryBreakdown } from '../features/inventory/InventoryBreakdown';
import {
  computeBreakdown,
  nextBreakdownDimension,
  type BreakdownSegment,
} from '../features/inventory/inventory-breakdown';
import { uniquePartTypes } from '../features/inventory/part-type-filter';
import { updateRangeSelection } from '../features/inventory/range-selection';
import { SavedViewDialog } from '../features/inventory/SavedViewDialog';
import { BulkCreateDrawer } from '../features/item-create/BulkCreateDrawer';
import { NewItemDrawer } from '../features/item-create/NewItemDrawer';
import { useAuth } from '../auth/AuthContext';
import { toMap } from '../lib/collections';
import { humanizeIdentifier, sortByDisplayLabel } from '../lib/formatting';
import { siteDisplayName, siteLabel, siteSearchAliases } from '../lib/site-label';
import {
  MOVED_ACTIVE_HINT,
  MOVED_ACTIVE_STATUS,
  statusForMove,
} from '../features/inventory/active-move';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const COLUMN_STORAGE_KEY = 'leatherback-inventory-columns-v2';
const IN_TRANSIT_STATUS_FILTER: ItemStatus[] = [
  'SHIPPED_TO_WAREHOUSE',
  'SHIPPED_TO_CUSTOMER',
  'RETURN_SHIPPED_TO_WAREHOUSE',
];
const WAREHOUSE_STATUS_FILTER: ItemStatus[] = ['DELIVERED_TO_WAREHOUSE', 'IN_WAREHOUSE_STORAGE'];
const alphabetizedItemStatuses = sortByDisplayLabel(itemStatuses, (status) => statusLabels[status]);

const columnDefinitions = [
  ['part', 'Part type / build'],
  ['hardwareRevision', 'Hardware revision'],
  ['status', 'Status'],
  ['customer', 'Customer'],
  ['assigned', 'Assigned site'],
  ['location', 'Current location'],
  ['attention', 'Attention'],
  ['inspected', 'Last inspection'],
  ['maintained', 'Last maintenance'],
  ['lastUsed', 'Last used'],
  ['modified', 'Last modified'],
  ['modifiedBy', 'Modified by'],
  ['version', 'Version'],
] as const;

type ColumnKey = (typeof columnDefinitions)[number][0];
type SortKey = 'id' | ColumnKey;
type BatchAction = 'edit' | 'maintenance';
type BatchAttentionAction = 'none' | 'clear' | 'set';
type CustomerScope = CustomerInventoryScope;

const customerScopeLabels: Record<CustomerScope, string> = {
  assigned: 'Assigned inventory',
  'on-site': 'Currently at customer sites',
  'away-from-assignment': 'Away from assigned site',
};

const defaultColumnKeys: ColumnKey[] = [
  'part',
  'status',
  'customer',
  'assigned',
  'location',
  'attention',
  'lastUsed',
  'modifiedBy',
];

function readVisibleColumns(): Set<ColumnKey> {
  const fallback = new Set(defaultColumnKeys);
  try {
    const saved = JSON.parse(window.localStorage.getItem(COLUMN_STORAGE_KEY) ?? '[]') as unknown;
    if (!Array.isArray(saved) || saved.length === 0) return fallback;
    return new Set(
      saved.filter((key): key is ColumnKey => columnDefinitions.some(([id]) => id === key)),
    );
  } catch {
    return fallback;
  }
}

function formatDate(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toISOString().slice(0, 10);
}

function sameStatuses(left: ItemStatus[], right: ItemStatus[]): boolean {
  return [...left].sort().join(',') === [...right].sort().join(',');
}

function statusFilterLabel(statuses: ItemStatus[]): string {
  if (sameStatuses(statuses, IN_TRANSIT_STATUS_FILTER)) return 'In transit';
  if (sameStatuses(statuses, WAREHOUSE_STATUS_FILTER)) return 'Warehouse';
  return statuses.map((status) => statusLabels[status]).join(', ');
}

function readCsvValues(value: string): string[] {
  return [
    ...new Set(
      value
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean),
    ),
  ];
}

function writeCsvFilter(
  params: URLSearchParams,
  pluralKey: string,
  legacyKey: string,
  values: readonly string[],
): void {
  params.delete(legacyKey);
  if (values.length > 0) params.set(pluralKey, values.join(','));
  else params.delete(pluralKey);
}

function attentionFilterLabel(sources: readonly AttentionSource[]): string {
  if (sources.length === attentionSourceValues.length) return 'Auto and manual';
  return sources.map((source) => attentionSourceLabels[source]).join(' and ');
}

export function InventoryPage() {
  const navigate = useNavigate();
  const { canMutate } = useAuth();
  const [params, setParams] = useSearchParams();
  const itemIdsParam = params.get('itemIds') ?? '';
  const customerIdsParam = params.get('customerIds') ?? params.get('customerId') ?? '';
  const siteIdsParam = params.get('siteIds') ?? params.get('siteId') ?? '';
  const partTypesParam = params.get('partTypes') ?? params.get('partType') ?? '';
  const itemIds = useMemo(() => readCsvValues(itemIdsParam), [itemIdsParam]);
  const customerIds = useMemo(() => readCsvValues(customerIdsParam), [customerIdsParam]);
  const siteIds = useMemo(() => readCsvValues(siteIdsParam), [siteIdsParam]);
  const selectedPartTypes = useMemo(() => readCsvValues(partTypesParam), [partTypesParam]);
  const customerId = customerIds[0] ?? '';
  const siteId = siteIds[0] ?? '';
  const statusParam = params.get('status') as ItemStatus | null;
  const explicitStatusesParam = params.get('statuses') ?? '';
  const explicitStatuses = useMemo(
    () =>
      explicitStatusesParam
        .split(',')
        .filter((status): status is ItemStatus => itemStatuses.includes(status as ItemStatus)),
    [explicitStatusesParam],
  );
  const attentionParam = params.get('attention');
  const attentionSources = useMemo(
    () =>
      attentionParam === 'true'
        ? [...attentionSourceValues]
        : attentionParam
          ? attentionParam
              .split(',')
              .filter((value): value is AttentionSource =>
                attentionSourceValues.includes(value as AttentionSource),
              )
          : [],
    [attentionParam],
  );
  const attention = attentionSources.length > 0;
  const family = params.get('family') as StatusFamily | null;
  const customerScopeParam = params.get('customerScope');
  const customerScope: CustomerScope | null =
    customerScopeParam === 'assigned' ||
    customerScopeParam === 'on-site' ||
    customerScopeParam === 'away-from-assignment'
      ? customerScopeParam
      : null;
  const assignedSiteId = params.get('assignedSiteId') ?? '';
  const locationSiteId = params.get('locationSiteId') ?? '';
  const partId = params.get('partId') ?? '';
  const sortParam = params.get('sort');
  const sortKey: SortKey | null =
    sortParam === 'id' || columnDefinitions.some(([key]) => key === sortParam)
      ? (sortParam as SortKey)
      : null;
  const sortDirection = params.get('dir') === 'desc' ? 'desc' : 'asc';
  const familyStatuses = useMemo(
    () => (family ? itemStatuses.filter((status) => statusFamilies[status] === family) : []),
    [family],
  );
  const scopedStatuses = useMemo(
    () => (explicitStatuses.length > 0 ? explicitStatuses : statusParam ? [] : familyStatuses),
    [explicitStatuses, familyStatuses, statusParam],
  );
  const serverKey = [
    itemIds.join(','),
    customerIds.join(','),
    customerScope ?? '',
    siteIds.join(','),
    assignedSiteId,
    locationSiteId,
    partId,
    selectedPartTypes.join(','),
    statusParam ?? '',
    explicitStatuses.join(','),
    attentionSources.join(','),
    family ?? '',
  ].join('|');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const selectionAnchorRef = useRef<string | null>(null);
  const shiftPressedRef = useRef(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [batchAction, setBatchAction] = useState<BatchAction | null>(null);
  // '' means "don't change status" -- unlike the single-item editor, a batch
  // edit has no one current value to default to across a mixed selection.
  const [batchStatusValue, setBatchStatusValue] = useState<ItemStatus | ''>('');
  const [batchLocationSiteId, setBatchLocationSiteId] = useState('');
  const [batchClearLocation, setBatchClearLocation] = useState(false);
  const [batchAssignedSiteId, setBatchAssignedSiteId] = useState('');
  const [batchAttentionAction, setBatchAttentionAction] = useState<BatchAttentionAction>('none');
  const [batchAttentionReason, setBatchAttentionReason] = useState('');
  const [batchMaintenanceDate, setBatchMaintenanceDate] = useState('');
  const [batchMaintenanceBy, setBatchMaintenanceBy] = useState('');
  const [batchMaintenanceNotes, setBatchMaintenanceNotes] = useState('');
  const [batchReviewChange, setBatchReviewChange] = useState<BatchChange | null>(null);
  const [savedViews, setSavedViews] = useState<SavedView[]>(() => loadSavedViews());
  const [creatingItem, setCreatingItem] = useState(false);
  const [bulkCreatingItems, setBulkCreatingItems] = useState(false);
  const [deletingSelected, setDeletingSelected] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(readVisibleColumns);

  // Changing a filter returns the operator to the first result page.
  useEffect(() => {
    setPageIndex(0);
    setSelected(new Set());
    selectionAnchorRef.current = null;
  }, [serverKey]);

  useEffect(() => {
    function trackShift(event: KeyboardEvent): void {
      if (event.key === 'Shift') shiftPressedRef.current = event.type === 'keydown';
    }
    function clearShift(): void {
      shiftPressedRef.current = false;
    }
    window.addEventListener('keydown', trackShift);
    window.addEventListener('keyup', trackShift);
    window.addEventListener('blur', clearShift);
    return () => {
      window.removeEventListener('keydown', trackShift);
      window.removeEventListener('keyup', trackShift);
      window.removeEventListener('blur', clearShift);
    };
  }, []);

  const parts = useQuery({ queryKey: ['parts'], queryFn: () => inventoryApi.listParts() });
  const availablePartTypes = useMemo(() => uniquePartTypes(parts.data ?? []), [parts.data]);
  // Reuse the full-fleet cache already mounted by AppShell. Filtering and
  // numbered pagination are derived locally over that one read-only snapshot,
  // so counts and page jumps remain accurate without a second fleet request.
  const items = useQuery({
    queryKey: FULL_FLEET_ITEMS_QUERY_KEY,
    queryFn: () => inventoryApi.listItems({ pageSize: FULL_FLEET_PAGE_SIZE }),
  });
  // One bulk call for the whole loaded fleet rather than one per item —
  // see Items.ListItemUsages's AIP-159 wildcard id ("-") in items.proto,
  // and tidaltech/koi#7812 for why this is a separate call from listItems
  // rather than a field on Item.
  const loadedItemIds = useMemo(
    () => (items.data?.data ?? []).map((item) => item.id),
    [items.data],
  );
  const usages = useQuery({
    queryKey: ['itemUsages', loadedItemIds],
    queryFn: () => inventoryApi.getItemUsages(loadedItemIds),
    enabled: loadedItemIds.length > 0,
  });
  const usagesById = useMemo<Record<string, ItemUsage>>(() => usages.data ?? {}, [usages.data]);
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => inventoryApi.listSites() });
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => inventoryApi.listCustomers(),
  });
  const sitesById = useMemo(() => toMap(sites.data), [sites.data]);
  const customersById = useMemo(() => toMap(customers.data), [customers.data]);
  const partsById = useMemo(() => toMap(parts.data), [parts.data]);
  const itemIdOptions = useMemo(
    () =>
      (items.data?.data ?? []).map((item) => ({
        value: item.id,
        label: item.id,
        aliases: item.identity ? [item.identity] : [],
      })),
    [items.data],
  );
  const sortedCustomers = useMemo(
    () =>
      sortByDisplayLabel(customers.data ?? [], (customer) => customer.displayName ?? customer.name),
    [customers.data],
  );
  const sortedSites = useMemo(
    () => sortByDisplayLabel(sites.data ?? [], (site) => siteDisplayName(site)),
    [sites.data],
  );
  const customerSites = useMemo(
    () =>
      sortByDisplayLabel(sitesForCustomers(sites.data ?? [], customerIds), (site) =>
        siteDisplayName(site),
      ),
    [customerIds, sites.data],
  );
  const selectedCustomer = customersById.get(customerId);
  const siteFilterLabel =
    customerIds.length === 1
      ? `All ${selectedCustomer?.displayName ?? selectedCustomer?.name ?? 'customer'} sites`
      : customerIds.length > 1
        ? 'All selected-customer sites'
        : 'All sites';

  useEffect(() => {
    if (customerIds.length === 0 || siteIds.length === 0 || !sites.isSuccess) return;
    const validSiteIds = siteIds.filter((id) => customerSites.some((site) => site.id === id));
    if (validSiteIds.length === siteIds.length) return;

    const next = new URLSearchParams(params);
    writeCsvFilter(next, 'siteIds', 'siteId', validSiteIds);
    setParams(next, { replace: true });
  }, [customerIds, customerSites, params, setParams, siteIds, sites.isSuccess]);

  const activeItemFilters = useMemo(
    () => ({
      itemIds,
      customerIds,
      customerScope,
      siteIds,
      assignedSiteId,
      locationSiteId,
      partId,
      partTypes: selectedPartTypes,
      statuses: statusParam ? [statusParam] : scopedStatuses,
      attentionSources,
    }),
    [
      assignedSiteId,
      attentionSources,
      customerIds,
      customerScope,
      itemIds,
      locationSiteId,
      partId,
      selectedPartTypes,
      scopedStatuses,
      siteIds,
      statusParam,
    ],
  );
  const unsortedFiltered = useMemo(
    () =>
      filterInventoryItems(items.data?.data ?? [], sites.data ?? [], partsById, activeItemFilters),
    [activeItemFilters, items.data, partsById, sites.data],
  );
  const filtered = useMemo(() => {
    if (!sortKey) return unsortedFiltered;
    const valueFor = (item: Item): string | number | boolean => {
      const assignedSite = sitesById.get(item.siteId);
      switch (sortKey) {
        case 'id':
          return item.id;
        case 'part': {
          const part = partsById.get(item.partId);
          return `${part?.type ?? ''} ${part?.build ?? ''}`;
        }
        case 'hardwareRevision':
          return item.hardwareRevision ?? '';
        case 'status':
          return item.status ?? 'STATUS_UNSPECIFIED';
        case 'customer': {
          const customer = customersById.get(assignedSite?.customerId ?? '');
          return customer?.displayName ?? customer?.name ?? '';
        }
        case 'assigned':
          return siteDisplayName(assignedSite, item.siteId);
        case 'location': {
          const location = sitesById.get(item.locationSiteId ?? item.siteId);
          return `${siteDisplayName(location, '')} ${item.location ?? ''}`;
        }
        case 'attention':
          return Boolean(item.needsAttention);
        case 'inspected':
          return item.inspectedDate ?? '';
        case 'maintained':
          return item.maintenanceDate ?? '';
        case 'lastUsed':
          return usagesById[item.id]?.lastUsed ?? '';
        case 'modified':
          return item.lastModified ?? '';
        case 'modifiedBy':
          return item.lastModifiedBy ?? '';
        case 'version':
          return item.version;
      }
    };
    return [...unsortedFiltered].sort((left, right) => {
      const leftValue = valueFor(left);
      const rightValue = valueFor(right);
      const compared =
        typeof leftValue === 'number' && typeof rightValue === 'number'
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue), undefined, {
              numeric: true,
              sensitivity: 'base',
            });
      return sortDirection === 'desc' ? -compared : compared;
    });
  }, [sortKey, sortDirection, sitesById, partsById, customersById, usagesById, unsortedFiltered]);

  const viewCounts = useMemo(() => {
    const sourceItems = items.data?.data ?? [];
    const sourceSites = sites.data ?? [];
    return {
      allItems: filterInventoryItems(sourceItems, sourceSites, partsById, {}).length,
      exceptions: filterInventoryItems(sourceItems, sourceSites, partsById, { attention: true })
        .length,
    };
  }, [items.data, partsById, sites.data]);

  const breakdown = useMemo(() => {
    const dimension = nextBreakdownDimension({
      ...activeItemFilters,
      partId,
      assignedSiteId,
    });
    if (!dimension) return null;
    return computeBreakdown(unsortedFiltered, dimension, { partsById, sitesById, customersById });
  }, [
    activeItemFilters,
    assignedSiteId,
    customersById,
    partId,
    partsById,
    sitesById,
    unsortedFiltered,
  ]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(pageIndex + 1, pageCount);
  const visibleItems = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [currentPage, filtered, pageSize],
  );

  useEffect(() => {
    if (pageIndex + 1 <= pageCount) return;
    setPageIndex(pageCount - 1);
  }, [pageCount, pageIndex]);

  function updateParam(key: string, value: string | undefined): void {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key === 'sort' && !value) next.delete('dir');
    setParams(next);
  }

  function updateStatuses(values: string[]): void {
    const next = new URLSearchParams(params);
    next.delete('status');
    next.delete('statuses');
    next.delete('family');
    if (values.length > 0) next.set('statuses', values.join(','));
    setParams(next);
  }

  function updateCustomers(values: string[]): void {
    const next = new URLSearchParams(params);
    writeCsvFilter(next, 'customerIds', 'customerId', values);
    next.delete('customerScope');
    const validSiteIds = siteIds.filter((id) => {
      const selectedSite = sitesById.get(id);
      return values.length === 0 || values.includes(selectedSite?.customerId ?? '');
    });
    writeCsvFilter(next, 'siteIds', 'siteId', validSiteIds);
    setParams(next);
  }

  function updateSites(values: string[]): void {
    const next = new URLSearchParams(params);
    writeCsvFilter(next, 'siteIds', 'siteId', values);
    setParams(next);
  }

  function updateItemIds(values: string[]): void {
    const next = new URLSearchParams(params);
    if (values.length > 0) next.set('itemIds', values.join(','));
    else next.delete('itemIds');
    setParams(next);
  }

  function updatePartTypes(values: string[]): void {
    const next = new URLSearchParams(params);
    next.delete('partId');
    writeCsvFilter(next, 'partTypes', 'partType', values);
    setParams(next);
  }

  function updateAttentionSources(values: string[]): void {
    const next = new URLSearchParams(params);
    const sources = values.filter((value): value is AttentionSource =>
      attentionSourceValues.includes(value as AttentionSource),
    );
    if (sources.length === 0) next.delete('attention');
    else if (sources.length === attentionSourceValues.length) next.set('attention', 'true');
    else next.set('attention', sources.join(','));
    setParams(next);
  }

  function selectBreakdownSegment(segment: BreakdownSegment): void {
    if (!breakdown) return;
    switch (breakdown.dimension) {
      case 'partType':
        updatePartTypes([segment.key]);
        return;
      case 'status':
        updateStatuses([segment.key]);
        return;
      case 'customer':
        updateCustomers([segment.key]);
        return;
      case 'site':
        updateSites([segment.key]);
        return;
    }
  }

  function clearPageSelection(): void {
    setSelected(new Set());
    selectionAnchorRef.current = null;
    setBatchAction(null);
  }

  function goToPage(page: number): void {
    clearPageSelection();
    setPageIndex(Math.min(Math.max(page, 1), pageCount) - 1);
  }

  function toggleRow(id: string, checked: boolean, shiftKey: boolean): void {
    const orderedIds = visibleItems.map((item) => item.id);
    const anchorId = selectionAnchorRef.current;
    setSelected((current) =>
      updateRangeSelection({
        selected: current,
        orderedIds,
        targetId: id,
        anchorId,
        checked,
        shiftKey: shiftKey || shiftPressedRef.current,
      }),
    );
    selectionAnchorRef.current = id;
  }

  function togglePage(): void {
    const pageIds = visibleItems.map((item) => item.id);
    const anySelected = pageIds.some((id) => selected.has(id));
    setSelected(anySelected ? new Set() : new Set(pageIds));
    selectionAnchorRef.current = anySelected ? null : (pageIds[0] ?? null);
    if (anySelected) setBatchAction(null);
  }

  function handleSaveView(name: string): void {
    setSavedViews(addSavedView(name, params.toString()));
    setSaveViewOpen(false);
  }

  function handleRemoveView(id: string): void {
    setSavedViews(removeSavedView(id));
  }

  function toggleColumn(key: ColumnKey): void {
    setVisibleColumns((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      window.localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify([...next]));
      return next;
    });
  }

  function updateSort(key: SortKey): void {
    const next = new URLSearchParams(params);
    if (sortKey === key) {
      if (sortDirection === 'asc') {
        next.set('dir', 'desc');
      } else {
        next.delete('sort');
        next.delete('dir');
      }
    } else {
      next.set('sort', key);
      next.set('dir', 'asc');
    }
    selectionAnchorRef.current = null;
    setParams(next);
  }

  const activeChips = [
    itemIds.length
      ? {
          key: 'itemIds',
          label:
            itemIds.length === 1
              ? `Item ID: ${itemIds[0]}`
              : `Item IDs: ${itemIds.length} selected`,
          onRemove: () => updateItemIds([]),
        }
      : null,
    customerIds.length
      ? {
          key: 'customers',
          label:
            customerIds.length === 1
              ? `Customer: ${customersById.get(customerId)?.displayName ?? customersById.get(customerId)?.name ?? customerId}`
              : `Customers: ${customerIds.length} selected`,
          onRemove: () => updateCustomers([]),
        }
      : null,
    customerScope && customerIds.length
      ? {
          key: 'customerScope',
          label: `Scope: ${customerScopeLabels[customerScope]}`,
          onRemove: () => updateParam('customerScope', undefined),
        }
      : null,
    siteIds.length
      ? {
          key: 'sites',
          label:
            siteIds.length === 1
              ? `Site: ${siteLabel(sitesById.get(siteId), siteId)}`
              : `Sites: ${siteIds.length} selected`,
          onRemove: () => updateSites([]),
        }
      : null,
    partId
      ? {
          key: 'partId',
          label: `Part definition: ${humanizeIdentifier(partsById.get(partId)?.type ?? partId)} (${partId})`,
          onRemove: () => updateParam('partId', undefined),
        }
      : null,
    selectedPartTypes.length
      ? {
          key: 'partTypes',
          label:
            selectedPartTypes.length === 1
              ? `Part type: ${humanizeIdentifier(selectedPartTypes[0] ?? '')}`
              : `Part types: ${selectedPartTypes.length} selected`,
          onRemove: () => updatePartTypes([]),
        }
      : null,
    statusParam
      ? {
          key: 'status',
          label: `Status: ${statusLabels[statusParam]}`,
          onRemove: () => updateParam('status', undefined),
        }
      : null,
    explicitStatuses.length
      ? {
          key: 'statuses',
          label: `Status: ${statusFilterLabel(explicitStatuses)}`,
          onRemove: () => updateStatuses([]),
        }
      : null,
    attention
      ? {
          key: 'attention',
          label: `Attention: ${attentionFilterLabel(attentionSources)}`,
          onRemove: () => updateAttentionSources([]),
        }
      : null,
    family
      ? {
          key: 'family',
          label: `Family: ${familyOrder.find((row) => row.family === family)?.label ?? family}`,
          onRemove: () => updateParam('family', undefined),
        }
      : null,
    assignedSiteId
      ? {
          key: 'assignedSiteId',
          label: `Assigned site: ${siteLabel(sitesById.get(assignedSiteId), assignedSiteId)}`,
          onRemove: () => updateParam('assignedSiteId', undefined),
        }
      : null,
    locationSiteId
      ? {
          key: 'locationSiteId',
          label: `Physical site: ${siteLabel(sitesById.get(locationSiteId), locationSiteId)}`,
          onRemove: () => updateParam('locationSiteId', undefined),
        }
      : null,
    sortKey
      ? {
          key: 'sort',
          label: `Sort: ${sortKey === 'id' ? 'Item ID' : columnDefinitions.find(([key]) => key === sortKey)?.[1]} (${sortDirection})`,
          onRemove: () => updateParam('sort', undefined),
        }
      : null,
  ].filter((chip): chip is { key: string; label: string; onRemove: () => void } => chip !== null);

  const narrowestFilter = activeChips[activeChips.length - 1];
  const narrowestFilterLabel = narrowestFilter?.label;

  const activeViewSearch = params.toString();
  const isLoading = items.isLoading || sites.isLoading || customers.isLoading || parts.isLoading;
  const hasError = items.isError || sites.isError || customers.isError || parts.isError;
  const selectedItems = visibleItems.filter((item) => selected.has(item.id));
  const anyPageSelected = selectedItems.length > 0;
  const allPageSelected = visibleItems.length > 0 && selectedItems.length === visibleItems.length;
  const activeSavedViewId = savedViews.find((view) => view.search === activeViewSearch)?.id;
  const resultCount = filtered.length;
  const resultLabel =
    resultCount === undefined
      ? 'Items'
      : `${resultCount.toLocaleString()} ${resultCount === 1 ? 'item' : 'items'}`;
  const inventoryResultLabel =
    resultCount === undefined
      ? 'Serialized items'
      : `${resultCount.toLocaleString()} serialized ${resultCount === 1 ? 'item' : 'items'}`;
  const inventoryScopeDescription =
    customerScope === 'assigned'
      ? `${inventoryResultLabel} assigned to this customer.`
      : customerScope === 'on-site'
        ? `${inventoryResultLabel} at this customer's sites.`
        : customerScope === 'away-from-assignment'
          ? `${inventoryResultLabel} away from the assigned site.`
          : `${inventoryResultLabel}.`;

  return (
    <>
      <PageHeader
        title={attention ? 'Exceptions' : 'Inventory'}
        description={
          attention
            ? `${resultLabel} with ${attentionFilterLabel(attentionSources).toLocaleLowerCase()}.`
            : inventoryScopeDescription
        }
        actions={
          <div className="button-group">
            <button
              type="button"
              onClick={() => setBulkCreatingItems(true)}
              disabled={!canMutate}
              title={canMutate ? undefined : 'Sign in to create items'}
            >
              Bulk create
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => setCreatingItem(true)}
              disabled={!canMutate}
              title={canMutate ? undefined : 'Sign in to create an item'}
            >
              New item
            </button>
          </div>
        }
      />

      <div className="inventory-toolbar">
        <div className="view-tabs">
          <Link
            to="/inventory"
            className={activeViewSearch === '' ? 'view-tab view-tab-active' : 'view-tab'}
            aria-current={activeViewSearch === '' ? 'page' : undefined}
          >
            All items <span className="view-tab-count">{viewCounts.allItems.toLocaleString()}</span>
          </Link>
          <Link
            to="/inventory?attention=true"
            className={attention && !activeSavedViewId ? 'view-tab view-tab-active' : 'view-tab'}
            aria-current={attention && !activeSavedViewId ? 'page' : undefined}
          >
            Exceptions{' '}
            <span className="view-tab-count">{viewCounts.exceptions.toLocaleString()}</span>
          </Link>
          {savedViews.map((view) => (
            <span
              key={view.id}
              className={activeSavedViewId === view.id ? 'view-tab view-tab-active' : 'view-tab'}
            >
              <Link
                to={`/inventory?${view.search}`}
                aria-current={activeSavedViewId === view.id ? 'page' : undefined}
              >
                {view.name}
              </Link>
              <button
                type="button"
                className="view-tab-remove"
                onClick={() => handleRemoveView(view.id)}
                aria-label={`Remove saved view ${view.name}`}
              >
                ×
              </button>
            </span>
          ))}
          <button type="button" className="view-tab-add" onClick={() => setSaveViewOpen(true)}>
            + Save view
          </button>
        </div>
        <div className="inventory-tools">
          <button
            type="button"
            onClick={() => setExportOpen(true)}
            disabled={filtered.length === 0}
          >
            Export
          </button>
          <button
            type="button"
            onClick={() => setColumnsOpen((open) => !open)}
            aria-expanded={columnsOpen}
          >
            Columns
            {visibleColumns.size < columnDefinitions.length
              ? ` · ${columnDefinitions.length - visibleColumns.size} hidden`
              : ''}
          </button>
          {columnsOpen ? (
            <div
              className="tool-popover columns-popover"
              role="dialog"
              aria-label="Choose inventory columns"
            >
              <div className="popover-heading">
                <strong>Visible columns</strong>
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => setColumnsOpen(false)}
                  aria-label="Close column chooser"
                >
                  ×
                </button>
              </div>
              <p className="empty-note">Item ID is always visible.</p>
              {columnDefinitions.map(([key, label]) => (
                <label className="checkbox-label" key={key}>
                  <input
                    type="checkbox"
                    checked={visibleColumns.has(key)}
                    onChange={() => toggleColumn(key)}
                  />
                  {label}
                </label>
              ))}
              <button
                type="button"
                onClick={() => {
                  const defaults = new Set(defaultColumnKeys);
                  setVisibleColumns(defaults);
                  window.localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify([...defaults]));
                }}
              >
                Reset to defaults
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <form
        className="filter-bar"
        onSubmit={(event) => event.preventDefault()}
        aria-label="Inventory filters"
      >
        <div className="filter-bar-row">
          <div className="filter-field filter-field-item-ids">
            <span className="filter-field-label">Item ID</span>
            <MultiSelectFilter
              ariaLabel="Item ID"
              onChange={updateItemIds}
              options={itemIdOptions}
              placeholder="All item IDs"
              requireSearchToBrowse
              values={itemIds}
            />
          </div>
          <div className="filter-field filter-field-customer">
            <span className="filter-field-label">Customer</span>
            <MultiSelectFilter
              ariaLabel="Customer"
              onChange={updateCustomers}
              options={sortedCustomers.map((customer) => ({
                value: customer.id,
                label: customer.displayName ?? customer.name,
                aliases: [customer.name, customer.id],
              }))}
              placeholder="All customers"
              values={customerIds}
            />
          </div>
          <div className="filter-field filter-field-site">
            <span className="filter-field-label">Site</span>
            <MultiSelectFilter
              ariaLabel="Site"
              onChange={updateSites}
              options={customerSites.map((site) => ({
                value: site.id,
                label: siteLabel(site),
                aliases: siteSearchAliases(site),
              }))}
              placeholder={siteFilterLabel}
              values={siteIds}
            />
          </div>
          <div className="filter-field filter-field-part">
            <span className="filter-field-label">Part type</span>
            <MultiSelectFilter
              ariaLabel="Part type"
              onChange={updatePartTypes}
              options={availablePartTypes.map((value) => ({
                value,
                label: humanizeIdentifier(value),
                aliases: [value],
              }))}
              placeholder="All part types"
              values={selectedPartTypes}
            />
          </div>
          <div className="filter-field filter-field-status">
            <span className="filter-field-label">Status</span>
            <MultiSelectFilter
              ariaLabel="Status"
              onChange={updateStatuses}
              options={alphabetizedItemStatuses.map((value) => ({
                value,
                label: statusLabels[value],
                aliases: [value],
              }))}
              placeholder="All statuses"
              values={statusParam ? [statusParam] : explicitStatuses}
            />
          </div>
          <div className="filter-field filter-field-attention">
            <span className="filter-field-label">Attention flags</span>
            <span
              className="filter-attention-control-group"
              role="group"
              aria-label="Attention source"
            >
              {attentionSourceValues.map((source) => {
                const selectedSource = attentionSources.includes(source);
                return (
                  <label
                    className={`filter-attention-control${selectedSource ? ' filter-attention-control-active' : ''}`}
                    key={source}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSource}
                      onChange={(event) => {
                        const nextSources = event.target.checked
                          ? [...attentionSources, source]
                          : attentionSources.filter((current) => current !== source);
                        updateAttentionSources(nextSources);
                      }}
                    />
                    <span>{attentionSourceLabels[source]}</span>
                  </label>
                );
              })}
            </span>
          </div>
        </div>
        <AppliedFilters filters={activeChips} />
      </form>

      {!isLoading && !hasError && breakdown ? (
        <InventoryBreakdown breakdown={breakdown} onSelect={selectBreakdownSegment} />
      ) : null}

      <div
        className={`selection-bar${anyPageSelected ? ' selection-bar-active' : ''}`}
        role="toolbar"
        aria-label="Bulk inventory actions"
      >
        <span className="selection-count" aria-live="polite">
          {selectedItems.length.toLocaleString()} selected on this page
        </span>
        <div className="selection-actions">
          <button
            type="button"
            disabled={!anyPageSelected}
            onClick={() => setBatchAction((current) => (current === 'edit' ? null : 'edit'))}
          >
            Edit selected
          </button>
          <button
            type="button"
            disabled={!anyPageSelected}
            onClick={() =>
              setBatchAction((current) => (current === 'maintenance' ? null : 'maintenance'))
            }
          >
            Record maintenance
          </button>
          <button
            type="button"
            className="button-danger"
            disabled={!anyPageSelected || !canMutate}
            title={canMutate ? undefined : 'Sign in to delete inventory'}
            onClick={() => setDeletingSelected(true)}
          >
            Delete selected
          </button>
          {batchAction === 'edit'
            ? (() => {
                const destination = sitesById.get(batchLocationSiteId);
                const assignedDestination = sitesById.get(batchAssignedSiteId);
                const attentionReasonRequired =
                  batchAttentionAction === 'set' && !batchAttentionReason.trim();
                const nothingToApply =
                  !batchStatusValue &&
                  batchAttentionAction === 'none' &&
                  !batchLocationSiteId &&
                  !batchAssignedSiteId;
                return (
                  <div
                    className="batch-popover batch-popover-combined"
                    role="dialog"
                    aria-label="Edit selected"
                  >
                    <p className="empty-note">Only changed fields are applied.</p>
                    <label>
                      Status
                      <SearchableSelect
                        ariaLabel="New status"
                        onChange={(value) => setBatchStatusValue(value as ItemStatus | '')}
                        options={[
                          { value: '', label: "Don't change" },
                          ...alphabetizedItemStatuses
                            .filter((value) => value !== 'STATUS_UNSPECIFIED')
                            .map((value) => ({
                              value,
                              label: statusLabels[value],
                              aliases: [value],
                            })),
                        ]}
                        placeholder="Don't change"
                        value={batchStatusValue}
                      />
                    </label>
                    <label>
                      Attention
                      <SearchableSelect
                        ariaLabel="Attention action"
                        onChange={(value) => setBatchAttentionAction(value as BatchAttentionAction)}
                        options={[
                          { value: 'none', label: "Don't change" },
                          { value: 'clear', label: 'Clear attention flag' },
                          { value: 'set', label: 'Flag for attention' },
                        ]}
                        placeholder="Don't change"
                        value={batchAttentionAction}
                      />
                    </label>
                    {batchAttentionAction === 'set' ? (
                      <label>
                        Shared reason
                        <input
                          value={batchAttentionReason}
                          onChange={(event) => setBatchAttentionReason(event.target.value)}
                          placeholder="Why do all selected items need attention?"
                        />
                      </label>
                    ) : null}
                    <label>
                      Current site
                      <SearchableSelect
                        ariaLabel="Current site"
                        onChange={setBatchLocationSiteId}
                        options={[
                          { value: '', label: "Don't change" },
                          ...sortedSites.map((site) => ({
                            value: site.id,
                            label: siteLabel(site),
                            aliases: siteSearchAliases(site),
                          })),
                        ]}
                        placeholder="Don't change"
                        value={batchLocationSiteId}
                      />
                    </label>
                    {batchLocationSiteId ? (
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={batchClearLocation}
                          onChange={(event) => setBatchClearLocation(event.target.checked)}
                        />
                        Clear each item&apos;s location detail
                      </label>
                    ) : null}
                    <label>
                      Assigned site
                      <SearchableSelect
                        ariaLabel="Assigned site"
                        onChange={setBatchAssignedSiteId}
                        options={[
                          { value: '', label: "Don't change" },
                          ...sortedSites.map((site) => ({
                            value: site.id,
                            label: siteLabel(site),
                            aliases: siteSearchAliases(site),
                          })),
                        ]}
                        placeholder="Don't change"
                        value={batchAssignedSiteId}
                      />
                    </label>
                    <div className="batch-popover-actions">
                      <button
                        type="button"
                        onClick={() => {
                          setBatchAction(null);
                          setBatchStatusValue('');
                          setBatchAttentionAction('none');
                          setBatchAttentionReason('');
                          setBatchLocationSiteId('');
                          setBatchClearLocation(false);
                          setBatchAssignedSiteId('');
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="primary"
                        disabled={nothingToApply || attentionReasonRequired}
                        onClick={() => {
                          const status = batchStatusValue;
                          const attentionAction = batchAttentionAction;
                          const reason = batchAttentionReason.trim();
                          const locationSiteId = batchLocationSiteId;
                          const clearLocation = batchClearLocation;
                          const assignedSiteId = batchAssignedSiteId;
                          const fields: { field: string; value: string }[] = [];
                          if (status) fields.push({ field: 'Status', value: statusLabels[status] });
                          if (attentionAction !== 'none') {
                            fields.push({
                              field: 'Attention',
                              value:
                                attentionAction === 'set'
                                  ? `Flag: ${reason}`
                                  : 'Clear flag and reason',
                            });
                          }
                          if (locationSiteId) {
                            fields.push({
                              field: 'Current location site',
                              value: `${siteLabel(destination, locationSiteId)}${clearLocation ? ' · clear detail' : ''}`,
                            });
                          }
                          if (assignedSiteId) {
                            fields.push({
                              field: 'Assigned site',
                              value: siteLabel(assignedDestination, assignedSiteId),
                            });
                          }
                          const siteChange = {
                            siteId: assignedSiteId || undefined,
                            locationSiteId: locationSiteId || undefined,
                          };
                          const movedActive = status
                            ? 0
                            : selectedItems.filter((item) => statusForMove(item, siteChange))
                                .length;
                          if (movedActive > 0) {
                            fields.push({
                              field: 'Status',
                              value: `${movedActive.toLocaleString()} Active → ${statusLabels[MOVED_ACTIVE_STATUS]}. ${MOVED_ACTIVE_HINT}`,
                            });
                          }
                          setBatchAction(null);
                          setBatchReviewChange({
                            action: 'Edit selected items',
                            fields,
                            buildPatch: (item) => {
                              const movedStatus = status
                                ? undefined
                                : statusForMove(item, siteChange);
                              return {
                                ...(status ? { status } : {}),
                                ...(movedStatus ? { status: movedStatus } : {}),
                                ...(attentionAction === 'set'
                                  ? { needsAttention: true, attentionReason: reason }
                                  : {}),
                                ...(attentionAction === 'clear'
                                  ? { needsAttention: false, attentionReason: '' }
                                  : {}),
                                ...(locationSiteId
                                  ? {
                                      locationSiteId,
                                      ...(clearLocation ? { location: '' } : {}),
                                    }
                                  : {}),
                                ...(assignedSiteId ? { siteId: assignedSiteId } : {}),
                              };
                            },
                          });
                        }}
                      >
                        Continue to review
                      </button>
                    </div>
                  </div>
                );
              })()
            : null}
          {batchAction === 'maintenance' ? (
            <div className="batch-popover" role="dialog" aria-label="Record maintenance">
              <label>
                Maintenance date
                <input
                  type="date"
                  value={batchMaintenanceDate}
                  onChange={(event) => setBatchMaintenanceDate(event.target.value)}
                />
              </label>
              <label>
                Maintained by
                <input
                  value={batchMaintenanceBy}
                  onChange={(event) => setBatchMaintenanceBy(event.target.value)}
                  placeholder="Optional actor or team"
                />
              </label>
              <label>
                Shared notes
                <textarea
                  value={batchMaintenanceNotes}
                  onChange={(event) => setBatchMaintenanceNotes(event.target.value)}
                  rows={2}
                  placeholder="Optional shared maintenance note"
                />
              </label>
              <div className="batch-popover-actions">
                <button type="button" onClick={() => setBatchAction(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="primary"
                  disabled={!batchMaintenanceDate}
                  onClick={() => {
                    const isoDate = new Date(`${batchMaintenanceDate}T00:00:00.000Z`).toISOString();
                    const actor = batchMaintenanceBy.trim();
                    const notes = batchMaintenanceNotes.trim();
                    setBatchAction(null);
                    setBatchReviewChange({
                      action: 'Record maintenance',
                      fields: [
                        {
                          field: 'Maintenance date, actor and notes',
                          value: `${batchMaintenanceDate}${actor ? ` · ${actor}` : ''}${notes ? ' · shared note' : ''}`,
                        },
                      ],
                      buildPatch: () => ({
                        maintenanceDate: isoDate,
                        ...(actor ? { maintenanceBy: actor } : {}),
                        ...(notes ? { maintenanceNotes: notes } : {}),
                      }),
                    });
                  }}
                >
                  Continue to review
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {batchReviewChange ? (
        <BatchUpdateDrawer
          key={[...selected].sort().join(',')}
          items={selectedItems}
          change={batchReviewChange}
          onClose={() => setBatchReviewChange(null)}
          onApplied={() => {
            setBatchReviewChange(null);
            setSelected(new Set());
            void items.refetch();
          }}
        />
      ) : null}

      {creatingItem ? (
        <NewItemDrawer
          parts={parts.data ?? []}
          sites={sites.data ?? []}
          onClose={() => setCreatingItem(false)}
          onCreated={(itemId) => {
            setCreatingItem(false);
            void navigate(`/inventory/${encodeURIComponent(itemId)}`);
          }}
        />
      ) : null}

      {bulkCreatingItems ? (
        <BulkCreateDrawer
          parts={parts.data ?? []}
          sites={sites.data ?? []}
          onClose={() => setBulkCreatingItems(false)}
          onCreated={() => {
            setBulkCreatingItems(false);
            void items.refetch();
          }}
        />
      ) : null}

      {deletingSelected ? (
        <DeleteItemsDialog
          items={selectedItems}
          onClose={() => setDeletingSelected(false)}
          onDeleted={() => {
            setDeletingSelected(false);
            setSelected(new Set());
            void items.refetch();
          }}
        />
      ) : null}

      {exportOpen ? (
        <ExportDialog
          items={visibleItems}
          partsById={partsById}
          sitesById={sitesById}
          customersById={customersById}
          onClose={() => setExportOpen(false)}
        />
      ) : null}

      {saveViewOpen ? (
        <SavedViewDialog
          search={activeViewSearch}
          onClose={() => setSaveViewOpen(false)}
          onSave={handleSaveView}
        />
      ) : null}

      {isLoading ? (
        <div className="inventory-loading-state">
          <PageLoadingState label="Loading inventory…" className="page-loading-inline" />
          <TableSkeleton />
        </div>
      ) : null}
      {hasError ? (
        <ErrorState
          rpcName="ListItems"
          onRetry={() => {
            void Promise.all([
              items.refetch(),
              sites.refetch(),
              customers.refetch(),
              parts.refetch(),
            ]);
          }}
        />
      ) : null}
      {!isLoading && !hasError && filtered.length === 0 ? (
        <div className="empty-state">
          <p>No items match these filters.</p>
          <div className="empty-state-actions">
            {narrowestFilter ? (
              <button type="button" onClick={narrowestFilter.onRemove}>
                Remove &quot;{narrowestFilterLabel}&quot;
              </button>
            ) : null}
            <button type="button" onClick={() => setParams(new URLSearchParams())}>
              Clear all filters
            </button>
          </div>
        </div>
      ) : null}

      {!isLoading && !hasError && filtered.length > 0 ? (
        <>
          <div className="table-wrap inventory-table-wrap">
            <table className={`inventory-table${attention ? ' inventory-table-exceptions' : ''}`}>
              <caption className="visually-hidden">
                {attention ? 'Items flagged for attention' : 'Serialized inventory results'}
              </caption>
              <thead>
                <tr>
                  <th scope="col" className="column-select">
                    <PageSelectionCheckbox
                      anySelected={anyPageSelected}
                      allSelected={allPageSelected}
                      onToggle={togglePage}
                      disabled={!canMutate}
                      title={canMutate ? undefined : 'Sign in to select items'}
                    />
                  </th>
                  <SortableHeader
                    label="Item ID"
                    sortKey="id"
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={updateSort}
                  />
                  {visibleColumns.has('part') ? (
                    <SortableHeader
                      label="Part type / build"
                      sortKey="part"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                  ) : null}
                  {visibleColumns.has('hardwareRevision') ? (
                    <SortableHeader
                      label="Hardware revision"
                      sortKey="hardwareRevision"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                  ) : null}
                  {visibleColumns.has('status') ? (
                    <SortableHeader
                      label="Status"
                      sortKey="status"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                  ) : null}
                  {visibleColumns.has('customer') ? (
                    <SortableHeader
                      label="Customer"
                      sortKey="customer"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                  ) : null}
                  {visibleColumns.has('assigned') ? (
                    <SortableHeader
                      label="Assigned site"
                      sortKey="assigned"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                  ) : null}
                  {visibleColumns.has('location') ? (
                    <SortableHeader
                      label="Current location"
                      sortKey="location"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                  ) : null}
                  {visibleColumns.has('attention') ? (
                    <SortableHeader
                      label="Attention"
                      sortKey="attention"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                  ) : null}
                  {visibleColumns.has('inspected') ? (
                    <SortableHeader
                      label="Inspected"
                      sortKey="inspected"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                  ) : null}
                  {visibleColumns.has('maintained') ? (
                    <SortableHeader
                      label="Maintained"
                      sortKey="maintained"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                  ) : null}
                  {visibleColumns.has('lastUsed') ? (
                    <SortableHeader
                      label="Last used"
                      sortKey="lastUsed"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                  ) : null}
                  {visibleColumns.has('modified') ? (
                    <SortableHeader
                      label="Modified"
                      sortKey="modified"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                  ) : null}
                  {visibleColumns.has('modifiedBy') ? (
                    <SortableHeader
                      label="Modified by"
                      sortKey="modifiedBy"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                  ) : null}
                  {visibleColumns.has('version') ? (
                    <SortableHeader
                      label="Version"
                      sortKey="version"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((item) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    part={partsById.get(item.partId)}
                    assignedSite={sitesById.get(item.siteId)}
                    locationSite={sitesById.get(item.locationSiteId ?? item.siteId)}
                    customer={customersById.get(sitesById.get(item.siteId)?.customerId ?? '')}
                    usage={usagesById[item.id]}
                    selected={selected.has(item.id)}
                    onToggle={(checked, shiftKey) => toggleRow(item.id, checked, shiftKey)}
                    canMutate={canMutate}
                    visibleColumns={visibleColumns}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <PageControls
            page={currentPage}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            total={filtered.length}
            onPageChange={goToPage}
            onPageSizeChange={(nextPageSize) => {
              setPageSize(nextPageSize);
              setPageIndex(0);
            }}
          />
        </>
      ) : null}
    </>
  );
}

function PageSelectionCheckbox({
  anySelected,
  allSelected,
  onToggle,
  disabled,
  title,
}: {
  anySelected: boolean;
  allSelected: boolean;
  onToggle: () => void;
  disabled: boolean;
  title: string | undefined;
}) {
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = anySelected && !allSelected;
    }
  }, [allSelected, anySelected]);

  return (
    <input
      ref={checkboxRef}
      type="checkbox"
      aria-label={anySelected ? 'Deselect all rows on this page' : 'Select all rows on this page'}
      checked={allSelected}
      onChange={onToggle}
      disabled={disabled}
      title={title}
    />
  );
}

function ItemRow({
  item,
  part,
  assignedSite,
  locationSite,
  customer,
  usage,
  selected,
  onToggle,
  canMutate,
  visibleColumns,
}: {
  item: Item;
  part: Part | undefined;
  assignedSite: Site | undefined;
  locationSite: Site | undefined;
  customer: Customer | undefined;
  usage: ItemUsage | undefined;
  selected: boolean;
  onToggle: (checked: boolean, shiftKey: boolean) => void;
  canMutate: boolean;
  visibleColumns: Set<ColumnKey>;
}) {
  const shiftSelectionRef = useRef(false);
  const lastUsedLocation =
    usage?.lastSiteName || usage?.lastPen
      ? `${usage.lastSiteName ?? ''} ${usage.lastPen ?? ''}`.trim()
      : undefined;
  const isDefect = (item.status ?? 'STATUS_UNSPECIFIED') === 'STATUS_UNSPECIFIED';
  const rowClassName = [selected ? 'row-selected' : '', isDefect ? 'row-defect' : '']
    .filter(Boolean)
    .join(' ');
  return (
    <tr className={rowClassName || undefined}>
      <td className="column-select">
        <input
          type="checkbox"
          checked={selected}
          onPointerDown={(event) => {
            shiftSelectionRef.current = event.shiftKey;
          }}
          onKeyDown={(event) => {
            if (event.key === ' ') shiftSelectionRef.current = event.shiftKey;
          }}
          onChange={(event) => {
            onToggle(event.target.checked, shiftSelectionRef.current);
            shiftSelectionRef.current = false;
          }}
          aria-label={`Select item ${item.id}`}
          title="Hold Shift to select a range"
          disabled={!canMutate}
        />
      </td>
      <th scope="row" className="column-id">
        <Link to={`/inventory/${encodeURIComponent(item.id)}`} className="mono">
          {item.id}
        </Link>
      </th>
      {visibleColumns.has('part') ? (
        <td className="column-part">
          {part ? humanizeIdentifier(part.type) : '—'}
          {part?.build ? <small>{humanizeIdentifier(part.build)}</small> : null}
        </td>
      ) : null}
      {visibleColumns.has('hardwareRevision') ? (
        <td className="column-hardwareRevision">{item.hardwareRevision || '—'}</td>
      ) : null}
      {visibleColumns.has('status') ? (
        <td className="column-status">
          <StatusBadge status={item.status} />
        </td>
      ) : null}
      {visibleColumns.has('customer') ? (
        <td className="column-customer">{customer?.displayName ?? customer?.name ?? '—'}</td>
      ) : null}
      {visibleColumns.has('assigned') ? (
        <td className="column-assigned">{siteLabel(assignedSite, item.siteId)}</td>
      ) : null}
      {visibleColumns.has('location') ? (
        <td className="column-location">
          {siteLabel(locationSite, item.locationSiteId ?? '—')}
          {item.location ? <small>{item.location}</small> : null}
        </td>
      ) : null}
      {visibleColumns.has('attention') ? (
        <td className="column-attention">
          {item.needsAttention ? <span className="attention">Needs attention</span> : '—'}
        </td>
      ) : null}
      {visibleColumns.has('inspected') ? (
        <td className="mono column-inspected">{formatDate(item.inspectedDate)}</td>
      ) : null}
      {visibleColumns.has('maintained') ? (
        <td className="mono column-maintained">{formatDate(item.maintenanceDate)}</td>
      ) : null}
      {visibleColumns.has('lastUsed') ? (
        <td
          className="mono column-lastUsed"
          title={lastUsedLocation}
        >
          {formatDate(usage?.lastUsed)}
          {lastUsedLocation ? <small>{lastUsedLocation}</small> : null}
        </td>
      ) : null}
      {visibleColumns.has('modified') ? (
        <td className="mono column-modified">{formatDate(item.lastModified)}</td>
      ) : null}
      {visibleColumns.has('modifiedBy') ? (
        <td className="truncate-cell column-modifiedBy" title={item.lastModifiedBy}>
          {item.lastModifiedBy ?? '—'}
        </td>
      ) : null}
      {visibleColumns.has('version') ? (
        <td className="mono numeric-cell column-version">{item.version}</td>
      ) : null}
    </tr>
  );
}

function SortableHeader({
  label,
  sortKey,
  activeKey,
  direction,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  activeKey: SortKey | null;
  direction: 'asc' | 'desc';
  onSort: (key: SortKey) => void;
}) {
  const active = activeKey === sortKey;
  return (
    <th
      scope="col"
      className={`column-${sortKey}`}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" className="sort-button" onClick={() => onSort(sortKey)}>
        {label}
        <span aria-hidden="true">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
      </button>
    </th>
  );
}
